<!-- src/views/admin/AdminOnboardingManageFormOptionsView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <!-- PREMIUM HEADER -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 no-print">
      <div class="bg-[#0A1630] rounded-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
        <div class="absolute top-0 right-0 p-12 opacity-5">
          <IdentificationIcon class="h-32 w-32 text-white" />
        </div>
        
        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Onboarding Registry</h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Master Configuration & Dropdown Logic</p>
          </div>
          
          <router-link to="/admin/onboarding-records" class="bg-white/5 border border-white/10 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
            <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return to Records</span>
          </router-link>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- LOADING STATE -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <svg class="animate-spin h-10 w-10 text-[#20FCF1]" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="micro-label mt-4">Hydrating Onboarding Logic...</p>
      </div>

      <div v-else-if="error" class="bg-rose-50 border border-rose-100 p-8 rounded-[32px] text-center">
        <p class="text-rose-600 font-bold mb-4">{{ error }}</p>
        <button @click="fetchOptions" class="bg-[#0A1630] text-white px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">Retry Connection</button>
      </div>
      
      <div v-else class="space-y-12">
        <!-- Loop through each FORM SECTION (Symmetrically reordered) [1.1.4] -->
        <div v-for="section in formSections" :key="section.title" class="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden">
          <div class="bg-slate-50 border-b border-slate-100 p-8">
            <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">{{ section.title }}</h2>
          </div>
          
          <div class="p-8 space-y-16">
            <!-- Loop through each OPTION GROUP -->
            <div v-for="optionGroupId in section.optionGroupIds" :key="optionGroupId">
              
              <div v-if="optionsDataMap[optionGroupId]">
                <!-- Group Header -->
                <div class="flex justify-between items-end mb-6 border-b border-slate-100 pb-4">
                  <div>
                    <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">{{ formatTitle(optionGroupId) }}</h3>
                    <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Registry ID: {{ optionGroupId }}</p>
                  </div>
                  <div v-if="optionsDataMap[optionGroupId].isClientSpecific" class="flex items-center gap-2">
                    <span class="text-[10px] font-black text-[#20FCF1] bg-[#EBFDF8] px-3 py-1 rounded-full uppercase tracking-widest">Client Specific</span>
                    <button @click="revertToGlobal(optionGroupId)" class="text-[10px] font-black text-rose-500 hover:underline uppercase tracking-widest">Reset</button>
                  </div>
                  <div v-else class="flex items-center gap-2">
                    <span class="text-[10px] font-black text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-widest">Global Default</span>
                  </div>
                </div>

                <!-- Option List -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  <div v-for="(option, index) in optionsDataMap[optionGroupId].options" :key="index" class="group flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#20FCF1] transition-all">
                    <div class="flex flex-col">
                      <span class="text-[9px] font-mono text-slate-400 uppercase tracking-tighter">{{ option.value }}</span>
                      <span class="text-xs font-bold text-[#0A1630] uppercase tracking-tight">{{ option.text }}</span>
                    </div>
                    <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button @click="openEditModal(optionGroupId, option)" class="p-2 text-slate-400 hover:text-[#0A1630] transition-colors">
                        <PencilSquareIcon class="h-4 w-4" />
                      </button>
                      <button @click="deleteOption(optionGroupId, option)" class="p-2 text-slate-400 hover:text-rose-500 transition-colors">
                        <TrashIcon class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div v-if="!optionsDataMap[optionGroupId].options?.length" class="col-span-full py-8 text-center border-2 border-dashed border-slate-100 rounded-2xl">
                    <p class="micro-label text-slate-300">No entries defined in this protocol</p>
                  </div>
                </div>

                <!-- Premium Dark Add Form -->
                <div class="bg-slate-900 rounded-[32px] p-6 shadow-2xl">
                  <form @submit.prevent="addOption(optionGroupId)" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label class="block text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] mb-2 ml-2">Internal Value</label>
                      <input 
                        type="text" 
                        v-model="newOptions[optionGroupId].value" 
                        placeholder="e.g. permanent_staff" 
                        required 
                        class="premium-dark-input" 
                      />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] mb-2 ml-2">Display Text</label>
                      <input 
                        type="text" 
                        v-model="newOptions[optionGroupId].text" 
                        placeholder="e.g. Permanent Staff" 
                        required 
                        class="premium-dark-input" 
                      />
                    </div>
                    <div class="flex items-end">
                      <button type="submit" :disabled="isProcessing" class="w-full h-[52px] bg-[#20FCF1] text-[#0A1630] rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50">
                        {{ isProcessing ? 'Processing...' : 'Add To Registry' }}
                      </button>
                    </div>
                  </form>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Modal -->
  <TransitionRoot as="template" :show="isEditModalOpen">
    <Dialog as="div" class="relative z-50" @close="isEditModalOpen = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-[#0A1630]/80 backdrop-blur-sm transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-[40px] bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <form @submit.prevent="saveOptionEdit">
                <div class="bg-white p-10">
                  <DialogTitle as="h3" class="text-xl font-black text-[#0A1630] uppercase tracking-tight mb-8">Refine Entry</DialogTitle>
                  <div class="space-y-6">
                     <div>
                        <label class="micro-label mb-2">Technical Reference (Locked)</label>
                        <input type="text" :value="editingOption.current.value" disabled class="premium-input bg-slate-50 text-slate-400" />
                    </div>
                    <div>
                        <label class="micro-label mb-2">Display Label</label>
                        <input type="text" v-model="editingOption.newText" required class="premium-input" />
                    </div>
                  </div>
                </div>
                <div class="bg-slate-50 p-8 flex flex-col sm:flex-row-reverse gap-4">
                  <button type="submit" :disabled="isProcessing" class="bg-[#20FCF1] text-[#0A1630] px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">
                    {{ isProcessing ? 'Synchronising...' : 'Update Entry' }}
                  </button>
                  <button type="button" class="text-[10px] font-black text-slate-400 uppercase tracking-widest" @click="isEditModalOpen = false">Cancel</button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PencilSquareIcon, TrashIcon, ArrowUturnLeftIcon, IdentificationIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { useAuth } from '@/composables/useAuth';
import { useUserStore } from '@/stores/userStore';

const { claims } = useAuth();
const userStore = useUserStore();
const functions = getFunctions(firebaseApp, 'europe-west2');
const manageFormOptions = httpsCallable(functions, 'manageFormOptions');

const loading = ref(true);
const error = ref(null);
const isProcessing = ref(false);
const optionsDataMap = ref({});
const newOptions = ref({});

// 🛡️ Symmetrical reordering and matching of form sections [1.1.4]
const formSections = ref([
  { title: '1. Your Details', optionGroupIds: ['employmentTypes', 'jobTitles', 'departments', 'sponsorshipTypes'] },
  { title: '2. Induction', optionGroupIds: ['inductionLocations'] },
  { title: '3. Responsibility Acknowledgement', optionGroupIds: ['rolesResponsibilitiesGroups'] },
  { title: '4. Literacy Test', optionGroupIds: ['literacyTestLevels'] },
  { title: '5. Health Assessment', optionGroupIds: ['healthAssessmentOutcomes'] },
  { title: '6. Eyesight Declaration', optionGroupIds: ['eyesightOutcomes'] },
  { title: '7. Right to Work', optionGroupIds: ['citizenshipTypes'] },
  { title: '8. PPE Issue', optionGroupIds: ['ppeItems'] },
  { title: '9. Primary Sponsor Contract', optionGroupIds: ['employmentType'] },
  { title: '10. Employee Handbook Acknowledgement', optionGroupIds: ['handbookVersions'] },
]);

const isEditModalOpen = ref(false);
const editingOption = reactive({
  groupId: null,
  current: { value: '', text: '' },
  newText: ''
});

const formatTitle = (id) => {
  if (!id) return '';
  if (id === 'employmentType') return 'Contract Categories';
  const spaced = id.replace(/([A-Z])/g, ' $1');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const fetchOptions = async () => {
  if (!claims.value?.clientId) return;

  loading.value = true;
  error.value = null;
  try {
    const clientId = claims.value.clientId;
    const allGroupIds = formSections.value.flatMap(section => section.optionGroupIds);
    const uniqueGroupIds = [...new Set(allGroupIds)];

    const dataMap = {};
    const newOpts = {};

    for (const id of uniqueGroupIds) {
      newOpts[id] = { value: '', text: '' };
      
      const docRef = doc(db, 'formOptions', id);
      const docSnap = await getDoc(docRef);

      let optionsToShow = [];
      let isClientSpecific = false;

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data[clientId] && Array.isArray(data[clientId])) {
          optionsToShow = data[clientId];
          isClientSpecific = true;
        } else if (data.global && Array.isArray(data.global)) {
          optionsToShow = data.global;
        }
      }

      optionsToShow.sort((a, b) => (a.text || '').localeCompare(b.text || ''));
      dataMap[id] = { id, options: optionsToShow, isClientSpecific };
    }

    optionsDataMap.value = dataMap;
    newOptions.value = newOpts;
  } catch (err) {
    console.error("Registry Sync Error:", err);
    error.value = `Failed to sync registry: ${err.message}`;
  } finally {
    loading.value = false;
  }
};

watch(() => claims.value, (newClaims) => {
  if (newClaims?.clientId) {
    fetchOptions();
  }
}, { immediate: true });

const getSanitizedList = (groupId) => {
    return JSON.parse(JSON.stringify(optionsDataMap.value[groupId]?.options || []));
};

// 🛡️ Expose Direct Firestore Writes to anyone with Command access clearance [1.1.4]
const saveListToDB = async (groupId, newList) => {
    const clientId = claims.value.clientId;
    if (userStore.hasAdminAccess) {
        const docRef = doc(db, 'formOptions', groupId);
        await setDoc(docRef, { [clientId]: newList }, { merge: true });
        return;
    }
    throw new Error("Administrative clearance required.");
};

const addOption = async (groupId) => {
  const clientId = claims.value?.clientId;
  const newOpt = newOptions.value[groupId];
  if (!newOpt.value || !newOpt.text || !clientId) return;
  
  isProcessing.value = true;
  const payload = { 
    value: newOpt.value.toLowerCase().replace(/\s+/g, '_').trim(), 
    text: newOpt.text.trim() 
  };
  
  try {
    if (userStore.hasAdminAccess) {
        const currentList = getSanitizedList(groupId);
        currentList.push(payload);
        await saveListToDB(groupId, currentList);
    } else {
        await manageFormOptions({ action: 'add', sectionId: groupId, clientId, option: payload });
    }
    newOptions.value[groupId] = { value: '', text: '' };
    await fetchOptions();
  } catch(e) {
    alert('Add Failure: ' + e.message);
  } finally {
    isProcessing.value = false;
  }
};

const deleteOption = async (groupId, optionToDelete) => {
  if (!confirm(`Authorise removal of "${optionToDelete.text}"?`)) return;
  const clientId = claims.value?.clientId;
  isProcessing.value = true;
  try {
    if (userStore.hasAdminAccess) {
        const currentList = getSanitizedList(groupId);
        const newList = currentList.filter(opt => opt.value !== optionToDelete.value);
        await saveListToDB(groupId, newList);
    } else {
        await manageFormOptions({ action: 'remove', sectionId: groupId, clientId, option: optionToDelete });
    }
    await fetchOptions();
  } catch(e) {
     alert('Delete Failure: ' + e.message);
  } finally {
    isProcessing.value = false;
  }
};

const saveOptionEdit = async () => {
  const clientId = claims.value?.clientId;
  const { groupId, current, newText } = editingOption;
  if (!clientId || !groupId) return;
  isProcessing.value = true;
  try {
    const updatedOption = { value: current.value, text: newText.trim() };
    if (userStore.hasAdminAccess) {
        const currentList = getSanitizedList(groupId);
        const index = currentList.findIndex(opt => opt.value === current.value);
        if (index !== -1) currentList[index] = updatedOption;
        await saveListToDB(groupId, currentList);
    } else {
        await manageFormOptions({ action: 'remove', sectionId: groupId, clientId, option: current });
        await manageFormOptions({ action: 'add', sectionId: groupId, clientId, option: updatedOption });
    }
    isEditModalOpen.value = false;
    await fetchOptions();
  } catch(e) {
    alert('Save Failure: ' + e.message);
  } finally {
    isProcessing.value = false;
  }
};

const revertToGlobal = async (groupId) => {
    if (!confirm('Revert category to global defaults? All customizations will be lost.')) return;
    const clientId = claims.value?.clientId;
    isProcessing.value = true;
    try {
        await manageFormOptions({ action: 'reset', sectionId: groupId, clientId });
        await fetchOptions();
    } catch(e) {
        alert('Reset Failure: ' + e.message);
    } finally {
        isProcessing.value = false;
    }
};

const openEditModal = (groupId, option) => {
    editingOption.groupId = groupId;
    editingOption.current = option;
    editingOption.newText = option.text;
    isEditModalOpen.value = true;
};
</script>

<style scoped>
.micro-label { @apply block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]; }
.premium-input { @apply mt-1 block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-[#0A1630] shadow-sm focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.premium-dark-input { @apply block w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.font-mono { font-family: ui-monospace, SFMono-Regular, Consolas, "Liberation Mono", monospace; }
</style>